import {
   recalculateWithoutDiscount,
   productCodeToCalculatedValuesPriceObject,
} from './calculate.mjs'
import { IsForeignCurrencyQuote, isAlwaysUSD } from './helpers.mjs'

/**
 * Calculate the ongoing monthly cost, excluding one-time products.
 * @param {Object} productCoreMonthlyCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} productPerformanceMonthlyCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} productTimeTrackingMonthlyCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} productBenefitsAdministrationMonthlyCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} AdditionalJobsCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} AdditionalStorageCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} TestAccountCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} MonthlyTaxCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} TimeClockMonthlyCost - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} MonthlyDynamicCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} productPayrollMonthlyTotalCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} productPayrollMonthlyStateTotalCosts - MonthlyPrice objects for each monthly product which factors into the activation total
 * @param {Object} product_list - Required inputs to perform a calculation.
 * @param {Object} discount_list - Required inputs to perform a calculation.
 * @param {String} book_id - Required inputs to perform a calculation.
 * @param {String} quoteType - Required inputs to perform a calculation.
 * @param {Boolean} coreProductSelected - Required inputs to perform a calculation.
 * @param {Number} productCoreEECount - Required inputs to perform a calculation.
 * @param {Number} productPerformanceEECount - Required inputs to perform a calculation.
 * @param {Number} productTimeTrackingEECount - Required inputs to perform a calculation.
 * @param {Boolean} hasTimeClocks - Required inputs to perform a calculation.
 * @param {Object} productTimeClockDetails - Required inputs to perform a calculation.
 * @param {Number} productBenefitsAdministrationEECount - Required inputs to perform a calculation.
 * @param {Number} productPayrollEINCount - Required inputs to perform a calculation.
 * @param {Number} productPayrollEECount - Required inputs to perform a calculation.
 * @param {Object} productPayrollEECounts - Required inputs to perform a calculation.
 * @param {Number} productPayrollTotalStateCount - Required inputs to perform a calculation.
 * @param {Object} productPayrollStateCounts - Required inputs to perform a calculation.
 * @param {Object} professionalServiceDetails - Required inputs to perform a calculation.
 * @param {Object} consultingServiceDetails - Required inputs to perform a calculation.
 * @param {String} impType - Required inputs to perform a calculation.
 * @param {String} benAdminImpType - Required inputs to perform a calculation.
 * @param {String} payrollImpType - Required inputs to perform a calculation.
 * @param {Boolean} discountBundle - Required inputs to perform a calculation.
 * @param {Boolean} discountNonProfit - Required inputs to perform a calculation.
 * @param {number} prepayLength - Prepayment length in months (1 = month-to-month).
 * @param {Boolean} discountMultiEntity - Required inputs to perform a calculation.
 * @param {Boolean} discountHRMonthlyPromotion - Required inputs to perform a calculation.
 * @param {Boolean} discountHRDealDeskMonthly - Required inputs to perform a calculation.
 * @param {Boolean} discountReseller - Required inputs to perform a calculation.
 * @param {Boolean} discountQuickstartTraining - Required inputs to perform a calculation.
 * @param {Boolean} discountImpPromotion - Required inputs to perform a calculation.
 * @param {Boolean} discountDealDeskHRImp - Required inputs to perform a calculation.
 * @param {Boolean} discountDealDeskPayrollImp - Required inputs to perform a calculation.
 * @param {Boolean} discountSecondEIN - Required inputs to perform a calculation.
 * @param {Boolean} discountGroupImp - Required inputs to perform a calculation.
 * @param {Number} discountDealDeskHRImpRate - Required inputs to perform a calculation.
 * @param {Number} discountDealDeskQuickstartImpRate - Required inputs to perform a calculation.
 * @param {Number} discountDealDeskPayrollImpRate - Required inputs to perform a calculation.
 * @param {Number} productAdditionalStorageCount - Required inputs to perform a calculation.
 * @param {Number} productAdditionalJobsCount - Required inputs to perform a calculation.
 * @param {Boolean} productHistoricalDataImport - Required inputs to perform a calculation.
 * @param {Boolean} productMarketplaceListing - Required inputs to perform a calculation.
 * @param {Boolean} productSponsorship - Required inputs to perform a calculation.
 * @param {Number} productPaidServiceAmount - Required inputs to perform a calculation.
 * @param {Boolean} productTestAccount - Required inputs to perform a calculation.
 * @param {Date} activationDate - Required inputs to perform a calculation.
 * @param {Boolean} differenceQuote - Required inputs to perform a calculation.
 * @param {Object} accountSnowflake - Required inputs to perform a calculation.
 * @param {Number} coreCountryCount - Required inputs to perform a calculation.
 * @param {Array<String>} promotionalDiscounts - Required inputs to perform a calculation.
 * @param {Date} nextInvoiceDate - Required inputs to perform a calculation.
 * @param {String} currencyObject - Required inputs to perform a calculation.
 * @param {Number} accountBalance - Required inputs to perform a calculation.
 * @param {Number} monthlyTax - Required inputs to perform a calculation.
 * @param {Object} productEINIntegrations - Required inputs to perform a calculation.
 * @param {Array<String>} allPermissions - Required inputs to perform a calculation.
 * @param {String} user_role - Required inputs to perform a calculation.
 * @param {String} account_status - Required inputs to perform a calculation.
 * @param {Object} pricebookDetails - Required inputs to perform a calculation.
 * @param {String} previousCurrencyObject - Required inputs to perform a calculation.
 * @param {Object} productPayrollEINImpTypes - Required inputs to perform a calculation.
 * @param {Boolean} prepaymentActivationUseDifference - Required inputs to perform a calculation.
 * @param {Array<Object>} dynamicProducts - Required inputs to perform a calculation.
 * @param {string} [selectedTimeProduct='timeTracking'] - The selected time product ('timeTracking' or 'timeAttendance').
 * @param {Boolean} [isATSOnly=false] - Flag indicating the quote is an ATS-only quote. Forwarded to recalculateWithoutDiscount.
 * @param {Boolean} [isRecalculation=true] - When true, skips the per-product discount-exclusion loop and returns the base ongoing total.
 * @returns {Array<Number>} The ongoing monthly cost, excluding one-time products, and excluding payroll only on foreign-currency quotes.
 */
/**
 * Returns true if the discount should be excluded from the ongoing monthly cost.
 * Excludes bundle discounts when bundle requirements aren't met, and discounts
 * that don't apply for at least 6 months within the first year.
 * @param {Object} discount - A discount object with DiscountCode, StartMonth, EndMonth
 * @param {Boolean} includeBundleDiscount - Whether bundle discount requirements are met
 * @returns {Boolean}
 */
export function shouldExcludeDiscount(discount, includeBundleDiscount) {
   if (discount.DiscountCode === 'bundleDiscount' && !includeBundleDiscount) {
      return true
   }
   const firstYearCoverage = Math.min(discount?.EndMonth ?? 120, 12) - (discount?.StartMonth ?? 0)
   return firstYearCoverage < 6
}

export function calculateOngoingMonthlyCost(
   //MonthlyPrice objects for each monthly product which factors into the activation total
   productCoreMonthlyCosts,
   productPerformanceMonthlyCosts,
   productTimeTrackingMonthlyCosts,
   productTimeAttendanceMonthlyCosts,
   productBenefitsAdministrationMonthlyCosts,
   AdditionalJobsCosts,
   AdditionalStorageCosts,
   TestAccountCosts,
   MonthlyTaxCosts,
   TimeClockMonthlyCost,
   MonthlyDynamicCosts,
   productPayrollMonthlyTotalCosts,
   productPayrollMonthlyStateTotalCosts,
   // Required inputs to perform a calculation.
   product_list,
   discount_list,
   book_id,
   quoteType,
   coreProductSelected,
   productCoreEECount,
   productPerformanceEECount,
   productTimeTrackingEECount,
   hasTimeClocks,
   productTimeClockDetails,
   productBenefitsAdministrationEECount,
   productPayrollEINCount,
   productPayrollEECount,
   productPayrollEECounts,
   productPayrollTotalStateCount,
   productPayrollStateCounts,
   professionalServiceDetails,
   consultingServiceDetails,
   impType,
   benAdminImpType,
   payrollImpType,
   discountBundle,
   discountNonProfit,
   prepayLength,
   discountMultiEntity,
   discountHRMonthlyPromotion,
   discountHRDealDeskMonthly,
   discountReseller,
   discountQuickstartTraining,
   discountImpPromotion,
   discountDealDeskHRImp,
   discountDealDeskPayrollImp,
   discountSecondEIN,
   discountGroupImp,
   discountDealDeskHRImpRate,
   discountDealDeskQuickstartImpRate,
   discountDealDeskPayrollImpRate,
   productAdditionalStorageCount,
   productAdditionalJobsCount,
   productHistoricalDataImport,
   productMarketplaceListing,
   productSponsorship,
   productPaidServiceAmount,
   productTestAccount,
   activationDate, // if not supplied, defaults to 1st day of month
   differenceQuote,
   accountSnowflake,
   coreCountryCount,
   promotionalDiscounts,
   nextInvoiceDate,
   currencyObject,
   accountBalance,
   monthlyTax,
   productEINIntegrations,
   allPermissions,
   user_role,
   account_status,
   pricebookDetails,
   previousCurrencyObject,
   productPayrollEINImpTypes,
   prepaymentActivationUseDifference,
   dynamicProducts,
   selectedTimeProduct = 'timeTracking',
   isATSOnly = false,
   isRecalculation = true,
) {
   //Create a Tuple of all the parameters to pass to the calculateQuote function
   // This is a workaround to avoid passing too many parameters directly.
   const calculateParams = [
      product_list,
      discount_list,
      book_id,
      quoteType,
      coreProductSelected,
      productCoreEECount,
      productPerformanceEECount,
      productTimeTrackingEECount,
      hasTimeClocks,
      productTimeClockDetails,
      productBenefitsAdministrationEECount,
      productPayrollEINCount,
      productPayrollEECount,
      productPayrollEECounts,
      productPayrollTotalStateCount,
      productPayrollStateCounts,
      professionalServiceDetails,
      consultingServiceDetails,
      impType,
      benAdminImpType,
      payrollImpType,
      discountBundle,
      discountNonProfit,
      prepayLength,
      discountMultiEntity,
      discountHRMonthlyPromotion,
      discountHRDealDeskMonthly,
      discountReseller,
      discountQuickstartTraining,
      discountImpPromotion,
      discountDealDeskHRImp,
      discountDealDeskPayrollImp,
      discountSecondEIN,
      discountGroupImp,
      discountDealDeskHRImpRate,
      discountDealDeskQuickstartImpRate,
      discountDealDeskPayrollImpRate,
      productAdditionalStorageCount,
      productAdditionalJobsCount,
      productHistoricalDataImport,
      productMarketplaceListing,
      productSponsorship,
      productPaidServiceAmount,
      productTestAccount,
      activationDate, // if not supplied, defaults to 1st day of month
      differenceQuote,
      accountSnowflake,
      coreCountryCount,
      promotionalDiscounts,
      nextInvoiceDate,
      currencyObject,
      accountBalance,
      monthlyTax,
      productEINIntegrations,
      allPermissions,
      user_role,
      account_status,
      pricebookDetails,
      previousCurrencyObject,
      productPayrollEINImpTypes,
      prepaymentActivationUseDifference,
      dynamicProducts,
      selectedTimeProduct,
   ]

   let monthlyProducts = [
      productCoreMonthlyCosts,
      productPerformanceMonthlyCosts,
      productTimeTrackingMonthlyCosts,
      productTimeAttendanceMonthlyCosts,
      productBenefitsAdministrationMonthlyCosts,
      AdditionalJobsCosts,
      AdditionalStorageCosts,
      TestAccountCosts,
      MonthlyTaxCosts,
      TimeClockMonthlyCost,
   ]

   if (!IsForeignCurrencyQuote(book_id)) {
      monthlyProducts.push(
         productPayrollMonthlyTotalCosts,
         productPayrollMonthlyStateTotalCosts,
      )
   }

   if (MonthlyDynamicCosts?.length > 0) {
      MonthlyDynamicCosts.forEach((product) => {
         if (
            product_list[product.productCode]?.BillingFreq === 'monthly' &&
            !(IsForeignCurrencyQuote(book_id) && isAlwaysUSD(product.productCode))
         ) {
            monthlyProducts.push(product)
         }
      })
   }

   let OngoingMonthlyTotal = 0
   let OngoingMonthlyTotalDiscount = 0
   //If the customer is not a current payroll customer or not all 3 products are on the quote, we exclude the bundle discount from the calculation

   const isCurrentPayrollCustomer =
      accountSnowflake.hasOwnProperty('Billed_Payroll__c') &&
      accountSnowflake.Billed_Payroll__c &&
      accountSnowflake.Billed_Payroll__c > 0
         ? true
         : false

   const allThreeBundleProductsOnQuote =
      productCoreEECount > 0 &&
      productPayrollEECount > 0 &&
      productBenefitsAdministrationEECount > 0

   const hasBundleDiscountNoRequirements =
      allPermissions['BundleDiscountNoRequirements']?.includes(user_role) ??
      false

   const includeBundleDiscount =
      isCurrentPayrollCustomer || allThreeBundleProductsOnQuote || hasBundleDiscountNoRequirements

   if (!isRecalculation) {
      for (const monthlyProduct of monthlyProducts) {
         let discountsToExclude = new Set()
         for (const discount of monthlyProduct.appliedDiscounts) {
            if (shouldExcludeDiscount(discount, includeBundleDiscount)) {
               discountsToExclude.add(discount.DiscountCode)
            }
         }
         let newOngoingRecalculatedValues = recalculateWithoutDiscount(
            discountsToExclude,
            // Required inputs to perform a calculation.
            ...calculateParams,
            isATSOnly,
            isRecalculation,
         )
         OngoingMonthlyTotal +=
            productCodeToCalculatedValuesPriceObject(
               newOngoingRecalculatedValues,
               monthlyProduct.productCode,
            )?.totalPrice ?? 0
         OngoingMonthlyTotalDiscount +=
            productCodeToCalculatedValuesPriceObject(
               newOngoingRecalculatedValues,
               monthlyProduct.productCode,
            )?.totalDiscount ?? 0
      }
   } else {
      OngoingMonthlyTotal = 0
      OngoingMonthlyTotalDiscount = 0
   }
   return [OngoingMonthlyTotal, OngoingMonthlyTotalDiscount]
}
